/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export type UserRole =
  | 'admin'
  | 'operator'
  | 'client'
  | 'ADMINISTRADOR'
  | 'GERENTE'
  | 'COORDINADOR'
  | 'SUPERVISOR'
  | 'OPERADOR'
  | 'CHOFER'
  | 'FACTURACION'
  | 'OPERACION';

export interface DC3NotificationSettings {
  enabled: boolean;
  channels: {
    browserPush: boolean; // Web push / Native Service Worker
    fcmPush: boolean; // Firebase Cloud Messaging push
    audioChime: boolean; // Synthesizer Chime / Audio Alert
    emailDigest: boolean; // Resumen / Alertas por Correo
    inAppBanner: boolean; // Banners visuales interactivos dentro de la plataforma
    whatsappAlerts: boolean; // Despacho de avisos a WhatsApp
  };
  frequency: 'realtime' | 'daily' | 'weekly' | 'biweekly' | 'monthly';
  dailyTime: string; // e.g. "08:00"
  weeklyDay: number; // 1 = Lunes, 5 = Viernes
  leadDays: {
    days60: boolean; // 60 días antes (Preventivo Temprano)
    days30: boolean; // 30 días antes (1 mes - Estándar STPS)
    days15: boolean; // 15 días antes (Crítico)
    days7: boolean; // 7 días antes (Urgente)
    days0: boolean; // El día del vencimiento
    expired: boolean; // Recurrente post-vencimiento
  };
  targetCostCenterMode: 'all' | 'assigned' | 'custom';
  customCostCenterIds?: string[];
  filterCriticalNormsOnly?: boolean; // Solo alertar NOMs críticas (NOM-009, NOM-017, NOM-018, etc.)
  minComplianceThresholdPercent?: number; // Ej: 90%
  emailRecipients?: string; // Correos adicionales separados por comas
  phoneRecipient?: string; // Teléfono para WhatsApp/SMS
  lastNotifiedAt?: string;
}

export interface UserProfile {
  uid: string;
  email: string;
  name: string;
  phone?: string;
  avatarUrl?: string;
  role: UserRole;
  password?: string; // Cleartext password for login verification
  clientCode?: string; // Associated client if client role
  assignedClientIds?: string[]; // Multiple assigned client IDs / codes
  assignedCostCenterIds?: string[]; // Multiple assigned cost center IDs / names
  dc3NotificationSettings?: DC3NotificationSettings;
  permissions: {
    canCreateFolios: boolean;
    canUpdateStages: boolean;
    canViewDashboard: boolean;
    canManageUsers: boolean;
    canBilling: boolean;
    menuAccess?: Record<string, boolean>; // Granular menu access checklist
  };
}

export const isClientRole = (role?: string): boolean => {
  if (!role) return false;
  const upper = role.trim().toUpperCase();
  // Specific client portal roles
  if (upper === 'OPERACION' || upper === 'OPERACIÓN' || upper === 'FACTURACION' || upper === 'FACTURACIÓN' || upper === 'CLIENT' || upper === 'CLIENTE') {
    return true;
  }
  const clientKeywords = [
    'CLIENT', 'CLIENTE', 'SOCIO', 'SOCIO COMERCIAL', 'EXTERNAL', 'EXTERNO', 
    'USUARIO_CLIENTE', 'PROVEEDOR', 'COMPRAS', 'ALMACEN_CLIENTE', 'FACTURACION', 'OPERACION'
  ];
  return clientKeywords.some(k => upper.includes(k));
};

export const isInternalRole = (role?: string): boolean => {
  if (!role) return false;
  const upper = role.trim().toUpperCase();
  // Client roles should never be treated as internal staff
  if (upper === 'OPERACION' || upper === 'OPERACIÓN' || upper === 'FACTURACION' || upper === 'FACTURACIÓN') {
    return false;
  }
  const internalKeywords = [
    'ADMINISTRADOR', 'ADMIN', 'GERENTE', 'COORDINADOR', 'SUPERVISOR', 
    'OPERADOR', 'CHOFER', 'OPERATOR', 'STAFF', 'INTERNO', 'LOGISTICA', 
    'MANTENIMIENTO', 'SISTEMAS', 'CALIDAD', 'ALMACEN', 'DESPACHADOR', 'INTERNAL',
    'CONTROLLER', 'FINANZAS', 'DIRECTOR', 'EJECUTIVO', 'VENTAS', 'COMERCIAL'
  ];
  return internalKeywords.includes(upper) || 
         upper.includes('ADMIN') || 
         upper.includes('STAFF') || 
         upper.includes('INTERN') || 
         upper.includes('SUPERV') || 
         upper.includes('GERENT') ||
         upper.includes('CONTROL') ||
         upper.includes('FINANZ') ||
         upper.includes('DIRECT');
};

export const isClientUser = (user?: { role?: string; clientCode?: string; assignedClientIds?: string[] } | null): boolean => {
  if (!user) return false;
  // Super-admin & Internal executive management have unrestricted total access and are never restricted as client-only
  if (isAdminRole(user.role)) return false;
  // If user has clientCode or assignedClientIds, they are client-bound
  if (user.clientCode && user.clientCode.trim().length > 0) return true;
  if (user.assignedClientIds && user.assignedClientIds.length > 0) return true;
  if (isClientRole(user.role)) return true;
  if (isInternalRole(user.role)) return false;
  return false;
};

export const isClientOperatorUser = (user?: { role?: string; clientCode?: string; assignedClientIds?: string[]; permissions?: any } | null): boolean => {
  if (!user) return false;
  if (isAdminRole(user.role)) return false;
  const roleUpper = (user.role || '').trim().toUpperCase();
  // Role OPERACION or OPERADOR on client side
  if (roleUpper === 'OPERACION' || roleUpper === 'OPERACIÓN' || roleUpper === 'OPERADOR_CLIENTE' || roleUpper === 'OPERACION_CLIENTE') {
    return true;
  }
  // Client user who is not in billing
  if (isClientUser(user)) {
    if (roleUpper === 'FACTURACION' || roleUpper === 'FACTURACIÓN') {
      return false;
    }
    // Any operator, operation, or client user without billing privileges is treated as client operator
    if (roleUpper === 'OPERADOR' || roleUpper === 'OPERATOR' || !user.permissions?.canBilling) {
      return true;
    }
    return true;
  }
  return false;
};

export const isClientBillingUser = (user?: { role?: string; clientCode?: string; assignedClientIds?: string[] } | null): boolean => {
  if (!user) return false;
  const roleUpper = (user.role || '').trim().toUpperCase();
  return isClientUser(user) && (roleUpper === 'FACTURACION' || roleUpper === 'FACTURACIÓN');
};

export const shouldHidePricesForUser = (user?: { role?: string; clientCode?: string; assignedClientIds?: string[]; permissions?: any } | null): boolean => {
  if (!user) return false;
  if (isAdminRole(user.role)) return false;
  // Choferes / drivers cannot see prices
  if (isChoferUser(user)) return true;
  // Client operator users cannot see prices (only materials and quantities)
  if (isClientOperatorUser(user)) return true;
  return false;
};

export const isAdminRole = (role?: string): boolean => {
  if (!role) return false;
  const upper = role.trim().toUpperCase();
  return [
    'ADMINISTRADOR', 'ADMIN', 'GERENTE', 'DIRECTOR', 'SISTEMAS', 
    'SUPERADMIN', 'CONTROL TOTAL', 'MASTER', 'DIRECTIVO'
  ].includes(upper) || upper.includes('ADMIN');
};

export const isChoferRole = (role?: string): boolean => {
  if (!role) return false;
  const upper = role.trim().toUpperCase();
  return upper === 'CHOFER' || upper === 'DRIVER' || upper === 'OPERADOR LOGISTICO' || upper === 'OPERADOR_LOGISTICO';
};

export const isChoferUser = (user?: { role?: string } | null): boolean => {
  if (!user) return false;
  return isChoferRole(user.role);
};

export interface Client {
  id: string;
  code: string;
  name: string;
  rfc: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  lat: number;
  lng: number;
  costCenterIds?: string[];
}

export interface CostCenter {
  id: string;
  clientId: string;
  clientName?: string;
  clientIds?: string[]; // Multiple associated client IDs (supports 2 or more clients per cost center)
  code: string;
  name: string;
  manager: string;
  address: string;
  lat: number;
  lng: number;
}

export interface ClientRouteInfo {
  clientId: string;
  clientName?: string;
  originName?: string;
  originAddress?: string;
  originLat?: number;
  originLng?: number;
  destinationName?: string;
  destinationAddress?: string;
  destLat?: number;
  destLng?: number;
  notes?: string;
}

export interface Destination {
  id: string;
  name: string;
  clientId?: string;
  clientName?: string;
  clientIds?: string[]; // Multiple associated client IDs (supports 2 or more clients per destination/route)
  clientRoutes?: ClientRouteInfo[]; // Detailed per-client Origin & Destination mappings
  originName?: string; // Nombre del Origen de la Ruta (ej. CEDIS Escobedo)
  originAddress?: string; // Dirección completa del Origen
  originLat?: number;
  originLng?: number;
  destinationName?: string; // Nombre específico del Destino
  address: string; // Dirección completa de Destino
  destinationAddress?: string;
  lat: number;
  lng: number;
  routeNotes?: string;
  estimatedKm?: number;
  estimatedMins?: number;
}

export interface GoodsItem {
  id: string;
  clientId?: string;
  code: string;
  description: string;
  unit: string;
  priceMXN: number | string;
  priceUSD: number | string; // Synced with FX
  weightKg?: number;
  imageUrl?: string;
  specifications?: string;
}

export type ProcessStage =
  | 'RECOLECCION'
  | 'ENTRADA_CEDIS'
  | 'DECAPADO_TERMICO_QUIMICO'
  | 'DECAPADO_LAVADO' // Skipped if granallado is true
  | 'INSPECCION'
  | 'RETRABAJO' // Optional if inspection fails
  | 'SALIDA_CEDIS'
  | 'ENTREGA_PLANTA'
  | 'DEVOLUCION'; // Optional at Entrega Planta

export interface StageHistory {
  stage: ProcessStage;
  timestamp: string;
  durationMs?: number; // Duration of this stage once completed
  notes?: string;
  operatorName: string;
  incidenceReport?: string; // For Rework
  reworkPiecesName?: string; // Piezas a retrabajar
  affectedPieces?: number; // Cantidad a retrabajar
  photos?: string[]; // Evidencia fotográfica (hasta 7 fotos)
  returnDetails?: string; // For Devolucion
  returnType?: 'TOTAL' | 'PARCIAL';
  returnedItems?: Array<{ goodsItemId?: string; description: string; quantityReturned: number; originalQuantity: number }>;
}

export type ProcessType = 'DECAPADO_TERMICO' | 'DECAPADO_QUIMICO' | 'DECAPADO_GRANALLADORA';

export interface Folio {
  id: string; // F-XXXXXX
  qrCodeUrl: string; // QR representation
  clientId: string;
  clientName: string;
  costCenterId: string;
  costCenterName: string;
  poNumber?: string; // Associated Purchase Order / Orden de Compra
  date?: string; // Fecha del Folio (YYYY-MM-DD o ISO)
  createdDate: string;
  createdBy: string;
  status: ProcessStage;
  processType?: ProcessType; // Tipo de proceso: Decapado Térmico, Químico o por Granalladora
  hasGranallado: boolean; // If true (Granalladora), skip DECAPADO_LAVADO and go directly to INSPECCION
  items: {
    goodsItemId: string;
    description: string;
    quantity: number;
    unitPriceMXN: number | string;
    unitPriceUSD: number | string;
    totalMXN: number | string;
    totalUSD: number | string;
    photoUrl?: string; // photo or base64
  }[];
  notes?: string;
  history: StageHistory[];
  etaMinutes?: number; // Active logistics ETA
  currentLat?: number;
  currentLng?: number;
  shipmentDate?: string; // Fecha de envío
  destination?: string; // Destino
  destinationId?: string; // ID del Destino registrado
  destinationLat?: number; // Latitud de destino
  destinationLng?: number; // Longitud de destino
  routeNotes?: string; // Puntos o notas de ruta
  estimatedKm?: number; // Distancia estimada
  origin?: string; // Origen (where the client is registered)
  originLat?: number; // Latitud de origen
  originLng?: number; // Longitud de origen
  photoUrl?: string; // Folio level evidence photo
  photoUrls?: string[]; // Multiple folio level evidence photos (up to 5)
  driverName?: string; // Nombre del chofer
  economicNumber?: string; // N° Económico
  deliveryDate?: string; // Fecha y hora en que se entregó a planta (YYYY-MM-DD o ISO)
  grStatus?: 'PENDIENTE' | 'CONFIRMADO'; // Status for GR confirmation in Billing
  grBillingDate?: string; // Fecha de corte / mes asignado para facturación del GR (YYYY-MM-DD)
  grConfirmedDate?: string; // Fecha y hora en que se confirmó el GR
}

export interface PreInvoice {
  id: string; // PRE-XXXX
  folioId: string;
  clientId: string;
  clientName: string;
  issueDate: string;
  subtotalMXN: number;
  subtotalUSD: number;
  taxMXN: number;
  taxUSD: number;
  totalMXN: number;
  totalUSD: number;
  status: 'Draft' | 'Sent' | 'Paid';
  items: {
    description: string;
    quantity: number;
    priceMXN: number | string;
    priceUSD: number | string;
    totalMXN: number | string;
    totalUSD: number | string;
  }[];
}

export interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  date: string; // YYYY-MM-DD
  type: 'pickup' | 'delivery' | 'billing' | 'meet';
  refId?: string; // folioId or meetId
}

export interface MeetMeeting {
  id: string;
  title: string;
  clientId: string;
  clientName: string;
  date: string;
  time: string;
  meetLink: string;
  notes?: string;
  meetingType?: string;
  requestedByClient?: boolean;
  transcription?: string;
  summary?: string;
  agreements?: string[];
  status: 'Requested' | 'Scheduled' | 'Completed' | 'Cancelled';
  createdAt?: string;
  updatedAt?: string;
}

export interface PoFolioSnapshot {
  folioId: string;
  createdDate: string;
  shipmentDate?: string;
  costCenterId?: string;
  costCenterName?: string;
  destination?: string;
  driverName?: string;
  economicNumber?: string;
  processType?: ProcessType;
  hasGranallado?: boolean;
  grStatus?: 'CONFIRMADO' | 'PENDIENTE';
  grBillingDate?: string;
  grConfirmedDate?: string;
  status?: ProcessStage;
  totalPieces: number;
  totalMXN: number;
  items: {
    description: string;
    quantity: number;
    unitPriceMXN: number;
    totalMXN: number;
  }[];
}

export interface PoRecord {
  id: string; // Document ID (e.g. PO-2026-BMW-01)
  poNumber: string;
  clientId: string;
  clientName: string;
  annualBudgetMXN: number;
  previousBalanceMXN: number;
  manualAdjustmentsMXN: number;
  totalNetBudgetMXN: number;
  totalDeliveredMXN: number;
  balanceMXN: number; // positive = Saldo a Favor, negative = Saldo Pendiente
  balanceType: 'SALDO_A_FAVOR' | 'SALDO_PENDIENTE';
  status: 'ABIERTA' | 'CERRADA' | 'EN_REVISION';
  linkedFolioIds: string[];
  encapsulatedFolios?: PoFolioSnapshot[];
  totalEncapsulatedPieces?: number;
  notes?: string;
  createdAt: string;
  lastUpdated: string;
  closedAt?: string;
  closedBy?: string;
  closureNotes?: string;
  carriedToNextPoNumber?: string;
  surplusCarriedMXN?: number;
  surplusOriginPoNumber?: string;
}

export interface ExchangeRate {
  rate: number; // USD to MXN
  lastUpdated: string;
}

export interface ActivityLog {
  id: string;
  timestamp: string;
  userEmail: string;
  action: string;
  details: string;
}

export interface EncapsulatedFolioInfo {
  folioId: string;
  clientName?: string;
  destination?: string;
  totalPieces?: number;
  totalWeightKg?: number;
  status?: ProcessStage;
}

export interface DriverLocation {
  id: string; // usually driver email or uid
  driverName: string;
  driverEmail: string;
  latitude: number;
  longitude: number;
  speed: number; // km/h
  lastUpdated: string; // ISO String
  active: boolean;
  plateNumber: string;
  folioId?: string; // associated active folio
  folioIds?: string[]; // array of encapsulated folio IDs
  encapsulatedFolios?: EncapsulatedFolioInfo[]; // detailed breakdown of encapsulated folios
  statusText?: string; // e.g. "En tránsito por Autopista 57"
}

export interface PendingAction {
  id: string;
  action: 'save' | 'delete';
  collectionName: string;
  targetId: string;
  data?: any;
  timestamp: string;
  description: string;
}

export interface SupportArea {
  id: string;
  name: string;
  agent: string;
  phone: string;
  desc: string;
  color?: string;
  clientId?: string;
  clientName?: string;
  clientIds?: string[];
  allClients?: boolean;
  costCenterIds?: string[];
  allCostCenters?: boolean;
}

export interface IncidentPhoto {
  id: string;
  name: string;
  dataUrl: string; // Base64
  caption?: string;
  uploadedAt: string;
}

export interface SafetyTalkPhoto {
  id: string;
  name: string;
  dataUrl: string; // Base64
  caption?: string;
  uploadedAt: string;
}

export type SafetyTalkStatus = 'programada' | 'aplicada' | 'no_realizada';

export interface SafetyTalkAttendee {
  index: number;
  name: string;
  accepted?: 'SI' | 'NO';
  signature?: string;
  date?: string;
}

export interface SafetyTalk {
  id: string;
  costCenterId?: string;
  costCenterName?: string;
  businessUnit?: string; // Unidad de Negocio (e.g. Bajío)
  project?: string; // Proyecto (e.g. Bajío)
  date: string; // YYYY-MM-DD
  day?: number; // 1-31
  time?: string; // HH:mm (e.g. 07:30)
  shift?: string; // Matutino, Vespertino, Nocturno, Mixto
  topic: string; // Nombre de la Plática
  standardCode?: string; // e.g. NOM-017-STPS / EPP
  area: string;
  facilitator: string; // Exponente
  facilitatorRole?: string;
  objective?: string; // Objetivo de la plática
  attendeesCount: number;
  attendees?: SafetyTalkAttendee[]; // Lista nominal de asistentes
  status: SafetyTalkStatus | 'completada'; // support 'completada' for legacy compat
  durationMinutes?: number;
  description: string;
  keyPoints?: string[];
  attendeesNotes?: string;
  observations?: string;
  photos?: SafetyTalkPhoto[]; // Up to 7 photos
  registeredBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type SafetyClassification = 
  | 'safe' 
  | 'near_miss' 
  | 'first_aid' 
  | 'medical_treatment' 
  | 'lost_time' 
  | 'environmental';

export interface SafetyIncident {
  id: string;
  costCenterId: string;
  costCenterName?: string;
  costCenterCode?: string;
  date: string; // YYYY-MM-DD
  day: number; // 1-31
  time: string; // HH:mm
  shift: string;
  area: string;
  classification: SafetyClassification;
  severity: 'Baja' | 'Media' | 'Alta' | 'Crítica';
  
  // Personal involucrado
  affectedPersonName: string;
  affectedPersonRole: string;
  affectedPersonEmployeeId: string;
  supervisorName: string;
  seniority?: string;
  
  // Lesión y atención médica
  affectedBodyPart: string;
  injuryType: string;
  medicalAttention: string;
  lostDays: number;
  ppeUsed: string;
  
  // Descripción y análisis causal
  title: string;
  description: string;
  unsafeAct: string;
  unsafeCondition: string;
  rootCause: string;
  
  // Acciones correctivas y preventivas
  immediateAction: string;
  preventiveAction: string;
  actionOwner: string;
  actionDeadline: string;
  actionStatus: 'Implementada' | 'En Proceso' | 'Pendiente';
  
  // Evidencia fotográfica (hasta 5 fotos)
  photos: IncidentPhoto[];
  
  // Metadata
  registeredBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CertificateAttachment {
  id: string;
  name: string;
  fileType: 'pdf' | 'word' | 'powerpoint' | 'image' | 'excel' | 'other';
  mimeType: string;
  sizeBytes?: number;
  sizeFormatted?: string;
  dataUrl: string; // Base64 data URL or storage URL
  uploadedAt: string;
  uploadedBy?: string;
  versionNumber?: number;
  notes?: string;
  isCurrentVersion?: boolean;
}

export interface CertificateHistoryEntry {
  id: string;
  date: string;
  type: 'creacion' | 'renovacion' | 'auditoria' | 'archivo_adjuntado' | 'cambio_estatus' | 'observacion';
  title: string;
  description: string;
  user?: string;
  attachmentId?: string;
  attachmentName?: string;
}

export interface CertificateRecord {
  id: string;
  name: string;
  issuer: string; // TÜV, SGS, BSI, AENOR, STPS, Protección Civil, etc.
  code: string; // ISO 9001:2015, ISO 14001, ISO 45001, etc.
  category: 'calidad' | 'ambiental' | 'seguridad' | 'metrologia' | 'proteccion_civil' | 'auditoria_cliente' | 'inocuidad' | 'otro';
  issueDate: string;
  validUntil: string; // Fecha de Vencimiento / Auditoría de Renovación
  status: 'activo' | 'proximo_vencer' | 'vencido' | 'en_auditoria' | 'en_renovacion';
  scope: string;
  costCenterId?: string;
  costCenterName?: string;
  leadAuditor?: string;
  auditType?: 'certificacion' | 'seguimiento' | 'recertificacion' | 'interna' | 'cliente';
  accreditationNumber?: string;
  cycleYears?: number;
  notification30DaysSent?: boolean;
  lastNotificationDate?: string;
  attachments?: CertificateAttachment[];
  history?: CertificateHistoryEntry[];
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type ILULevel = 'I' | 'L' | 'U' | 'O';

export interface SafetyActivity {
  id: string;
  code: string; // e.g. 'ACT-SEG-001', 'LOTO-01', 'ALT-02'
  name: string; // e.g. 'Bloqueo y Etiquetado de Energía (LOTO)'
  category: string; // e.g. 'Trabajos Peligrosos', 'Sustancias Químicas', 'Maquinaria', 'Emergencias'
  area: string; // e.g. 'Planta General', 'Mantenimiento', 'Producción'
  riskLevel: 'bajo' | 'medio' | 'alto' | 'critico';
  description?: string;
  eppRequired?: string; // e.g. 'Casco, Lentes, Guantes de Nitrilo, Arnés'
  standardCode?: string; // e.g. 'NOM-004-STPS-1999', 'NOM-009-STPS-2011'
  costCenterId?: string;
  costCenterName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CollaboratorEvaluation {
  id: string;
  activityId: string;
  activityCode: string;
  activityName: string;
  employeeId: string; // Nómina o ID del colaborador
  employeeName: string;
  position: string; // Puesto / Rol
  area: string; // Área operativa
  shift: string; // 'Turno 1 - Matutino' | 'Turno 2 - Vespertino' | 'Turno 3 - Nocturno' | 'Turno Mixto' | 'Administrativo'
  level: ILULevel; // 'I' (Inducción), 'L' (Supervisado), 'U' (Autónomo), 'O' (Instructor)
  score?: number; // 0 - 100%
  evaluationDate: string; // YYYY-MM-DD
  evaluator: string; // Nombre del Evaluador / Auditor de Seguridad
  status: 'apto' | 'en_desarrollo' | 'requiere_refuerzo' | 'no_apto';
  nextEvaluationDate?: string;
  evidenceNotes?: string;
  costCenterId?: string;
  costCenterName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CompetencyItem {
  id: string;
  employeeName: string;
  role: string;
  area: string;
  skills: {
    name: string;
    level: 1 | 2 | 3 | 4;
  }[];
  overallScore: number;
}

export interface DC3Record {
  id: string;
  costCenterId?: string;
  costCenterName?: string;
  employeeName: string;
  employeeId?: string;
  curp?: string;
  role: string;
  occupationCode?: string;
  courseName: string;
  thematicArea?: string;
  trainer: string;
  stpsReg: string;
  trainerType?: 'interno' | 'externo';
  startDate?: string;
  endDate?: string;
  issueDate: string;
  expiryDate: string;
  status: 'vigente' | 'por_renovar' | 'vencido';
  durationHours: number;
  score?: number | string;
  scannedDocumentUrl?: string;
  scannedDocumentName?: string;
  legalRepresentativeName?: string;
  workerRepresentativeName?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type ProjectStatus = 'EN_TIEMPO' | 'EN_RIESGO' | 'RETRASADO' | 'COMPLETADO' | 'POR_INICIAR' | 'PAUSADO';
export type ProjectPriority = 'ALTA' | 'MEDIA' | 'BAJA' | 'CRITICA';
export type TaskStatus = 'POR_INICIAR' | 'EN_PROCESO' | 'EN_REVISION' | 'COMPLETADA' | 'BLOQUEADA' | 'RETRASADA';

export interface ProjectTask {
  id: string;
  projectId: string;
  wbsCode: string; // e.g. "1.1", "2.3"
  name: string; // Task description
  phase: string; // e.g. "Fase 1: Diagnóstico e Ingeniería"
  assignee: string; // Responsible person
  assigneeRole?: string; // e.g. "Ing. Mecánico"
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  durationDays: number;
  progressPercent: number; // 0 - 100
  status: TaskStatus;
  priority: ProjectPriority;
  deliverable?: string; // e.g. "Reporte técnico firmado"
  dependencies?: string[]; // IDs of tasks that precede this
  isMilestone?: boolean;
  notes?: string;
}

export interface ProjectItem {
  id: string;
  code: string; // e.g. "PRJ-BAJ-2025-01"
  name: string; // Project title
  businessUnit: string; // e.g. "Bajío", "Aguascalientes"
  costCenterId?: string;
  costCenterName?: string;
  clientName: string; // e.g. "Mazda Salamanca"
  category: 'MANTENIMIENTO' | 'AUTOMATIZACION' | 'OBRA_CIVIL' | 'INSTALACION_QUIMICA' | 'HSEQ' | 'LOGISTICA' | 'SERVICIOS_ESPECIALIZADOS';
  manager: string; // Project Leader / PM
  sponsor?: string;
  startDate: string; // YYYY-MM-DD
  targetEndDate: string; // YYYY-MM-DD
  actualEndDate?: string;
  status: ProjectStatus;
  priority: ProjectPriority;
  budgetTotal?: number;
  budgetCurrency?: 'MXN' | 'USD';
  description: string;
  scopeObjectives?: string;
  progressPercent: number; // 0 - 100 calculated from tasks or manual
  tasks: ProjectTask[];
  createdAt: string;
  updatedAt?: string;
}

export type IncidentType = 'RETRABAJO' | 'DEVOLUCION' | 'SCRAP';
export type IncidentOrigin = 'INSPECCION' | 'ENTREGA_PLANTA' | 'SCRAP_ITEM_DANADO';
export type IncidentStatus = 'PENDIENTE' | 'EN_REPROCESO' | 'REINSPECCIONADO' | 'RESUELTO' | 'DESECHADO';
export type IncidentPriority = 'BAJA' | 'MEDIA' | 'ALTA' | 'CRITICA';
export type ResponsibleDept = 'CALIDAD' | 'DECAPADO_PINTURA' | 'LOGISTICA_TRANSPORTE' | 'PRODUCCION' | 'CLIENTE';

export interface ReworkIncident {
  id: string; // INC-2026-001 or Folio ID
  folioId: string;
  clientId: string;
  clientName: string;
  costCenterId: string;
  costCenterName: string;
  type: IncidentType;
  originStage: IncidentOrigin;
  causeCategory: string;
  causeDetail: string;
  affectedPieces: number;
  reworkPiecesName?: string;
  costEstimateMXN: number;
  responsibleDept: ResponsibleDept;
  priority: IncidentPriority;
  status: IncidentStatus;
  reportedBy: string;
  dateReported: string;
  actionPlan?: string;
  resolutionNotes?: string;
  photoUrl?: string;
  photos?: string[];
}

declare global {
  interface Window {
    showAppAlert: (message: string, title?: string) => Promise<void>;
    showAppConfirm: (message: string, title?: string) => Promise<boolean>;
  }
}

